import { type CSSProperties,useEffect,useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { ROUTE_PATHS } from '../../routes/routePaths';
import authService from '../../services/authService';
import { useAuthViewDocumentMutation } from '../../services/singleReportService';
import type {
  MtobResourceDocumentsResponse,
  RenderingStyleDocument,
  RenderingStyleDocumentationResponse,
  VendorUserResponse,
} from '../../types/auth';
import { isPdfExtension } from '../../util/downloadRedirect';
import { useDocumentDownload } from '../../util/useDocumentDownload';

interface TermsAndConditionsResponse {
  data: string;
}

  // Split documents into two columns
  const splitDocumentsIntoColumns = (documentation: Array<RenderingStyleDocument>) => {
    const columnCount = Math.ceil(documentation.length / 2);
    const leftColumn = documentation.slice(0, columnCount);
    const rightColumn = documentation.slice(columnCount);
    return { leftColumn, rightColumn };
  };

const resolveRenderStyleId = (document_: {
  renderStyleId?: number;
  renderStyleID?: number;
  renderingStyleId?: number;
}): number | undefined =>
  document_.renderStyleId ?? document_.renderStyleID ?? document_.renderingStyleId;

const resolveDocumentExtension = (document_: {
  extension?: string;
  fileName?: string;
}): string => {
  const extension = document_.extension?.trim();
  if (extension) return extension;
  const fileName = document_.fileName?.trim();
  if (!fileName) return '';
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.slice(dotIndex + 1) : '';
};

const getDocumentHref = (document_: any): string => {
  if (document_.isLink && document_.urlLink) return document_.urlLink;
  const documentId = document_.docId ?? document_.docID;
  const extension = resolveDocumentExtension(document_);
  // if (!documentId || !extension || !isPdfExtension(extension)) return '#';
  const parameters = new URLSearchParams();
  parameters.set('filename', `${documentId}.${extension}`);
  if (document_.fileDir) parameters.set('path', document_.fileDir);
  const renderStyleId = resolveRenderStyleId(document_);
  if (renderStyleId != null) parameters.set('renderStyleId', String(renderStyleId));
  parameters.set('location', document_.location ?? 'tc');
  if (document_.isMtob != null) parameters.set('isMtob', String(document_.isMtob));
  parameters.set('extension', extension);
  return `${ROUTE_PATHS.CONDITION_OF_USE_DOC_DOWNLOAD}?${parameters.toString()}`;
};

const handleDocumentLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  document_: any,
): void => {
  if (document_.isLink && document_.urlLink) return;
  if (isPdfExtension(resolveDocumentExtension(document_))) return;
  event.preventDefault();
};
const buildMtobDocument = (
  document_: any,
  fallbackRenderStyleId?: number,
) => ({
  ...document_,
  renderingStyleId: resolveRenderStyleId(document_) ?? fallbackRenderStyleId,
  location: document_.propertyName ? 'mastermtob' : 'tc',
  isMtob: 1,
});

interface ConditionOfUseModalProps {
  /** Style override for the "Conditions of Use" trigger link — e.g. white text
   * for the error pages' dark footer variant, which this component's own
   * default link styling wouldn't be visible against. */
  readonly linkStyle?: CSSProperties;
}

function ConditionOfUseModal({ linkStyle }: ConditionOfUseModalProps) {
  const [show, setShow] = useState(false);
   const storageMutation = useAuthViewDocumentMutation();
  const [lastRequestedFilename, setLastRequestedFilename] = useState<
    string | null
  >(null);

  const handleClose = () => setShow(false);
  const handleShow = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShow(true);
  };
  const sendDocumentData = (document_:any) => {
    const path = document_.fileDir;
    const documentId = document_.docId ?? document_.docID;
    const extension = resolveDocumentExtension(document_);
    const filename = documentId && extension ? `${documentId}.${extension}` : '';
    const renderStyleId = resolveRenderStyleId(document_);
    const downloadDocumentName = `${document_.docName ?? document_.fileName ?? 'Document'}.${extension}`;
    setLastRequestedFilename(downloadDocumentName);
    storageMutation.mutate({
      filename,
      path,
      renStyleID: renderStyleId,
      type: 'conditionsOfUse',
      compare: 0,
      prodID: renderStyleId,
      location: document_.location ?? 'tc',
      isMtob: document_.isMtob,
    });
  };
    useDocumentDownload({
      storageMutation,
      lastRequestedFilename,
      setLastRequestedFilename,
      skipErrorHandling: true, // Handle error separately to open in new tab
    });

    // Handle error separately - open in new tab for this component
    useEffect(() => {
      if (storageMutation.isError) {
        const status = (storageMutation.error as AxiosError | undefined)?.response?.status;
        let query = 'source=conditionsOfUse';
        if (status === 403) {
          const errorUrl = ROUTE_PATHS.PUBLIC_ACCESS_ERROR;
          window.open(errorUrl, '_blank', 'noopener,noreferrer');
          storageMutation.reset?.();
          return;
        } else if (status === 404) {
          query += '&isDocumentError=true';
        } else {
          query += '&globalError=true';
        }
        const errorUrl = `${ROUTE_PATHS.PUBLIC_STANDARD_ERROR}?${query}`;
        window.open(errorUrl, '_blank', 'noopener,noreferrer');
        storageMutation.reset?.();
      }
    }, [storageMutation.isError, storageMutation]);
  // Query for vendor user status
  const {
    data: vendorUserResponse,
    isLoading: isLoadingVendorUser,
    error: vendorUserError,
  } = useQuery<VendorUserResponse>({
    queryKey: ['vendorUsers'],
    queryFn: async () => {
      return await authService.getVendorUsers() as VendorUserResponse;
    },
    enabled: show,
  });

  const isVendorUser = (vendorUserResponse?.data?.length ?? 0) > 0;

  // Query for terms and conditions content
  const {
    data: termsResponse,
    isLoading: isLoadingTerms,
    error: termsError,
  } = useQuery<TermsAndConditionsResponse>({
    queryKey: ['termsAndConditions'],
    queryFn: async () => {
      return await authService.getTermsCondition() as TermsAndConditionsResponse;
    },
    enabled: show,
  });

  const termsContent = termsResponse?.data ?? '';

  // Query for rendering style documentation (only fetch if not vendor user and first query is done)
  const {
    data: documentationResponse,
    isLoading: isLoadingDocumentation,
    error: documentationError,
  } = useQuery<RenderingStyleDocumentationResponse>({
    queryKey: ['renderingStyleResourceDocs'],
    queryFn: async () => {
      return await authService.getRenderingStyleResourceDocs() as RenderingStyleDocumentationResponse;
    },
    enabled: show && !isLoadingVendorUser && !isVendorUser,
  });

  const {
    data: mtobResponse,
    isLoading: isLoadingMtob,
    error: mtobError,
  } = useQuery<MtobResourceDocumentsResponse>({
    queryKey: ['mtobResourceDocs'],
    queryFn: async () => {
      return await authService.getMtobResourceDocs() as MtobResourceDocumentsResponse;
    },
    enabled: show && !isLoadingVendorUser && !isVendorUser,
  });

  const renderingStyleDocumentation = documentationResponse?.data ?? [];
  const mtobProductSchedules = mtobResponse?.data?.productSchedules ?? [];
  const mtobMasterTermsOfBusiness = mtobResponse?.data?.masterTermsOfBusiness ? {...mtobResponse?.data?.masterTermsOfBusiness, extension: 'pdf'} : null;
  const mtobFallbackRenderStyleId = mtobProductSchedules.find(
    (schedule) => schedule.renderingStyleId != null,
  )?.renderingStyleId;
  const mtobDocuments = [
    ...(mtobResponse?.data?.masterTermsOfBusiness ? [mtobMasterTermsOfBusiness] : []),
    ...mtobProductSchedules,
  ];
  console.log(mtobDocuments);
  const mtobColumnCount = Math.ceil(mtobDocuments.length / 2);
  const mtobLeftColumn = mtobDocuments.slice(0, mtobColumnCount);
  const mtobRightColumn = mtobDocuments.slice(mtobColumnCount);

  const loading = isLoadingVendorUser ?? isLoadingTerms  ?? isLoadingDocumentation ?? isLoadingMtob;
  const error = vendorUserError ?? termsError ?? documentationError ?? mtobError;


  const { leftColumn, rightColumn } = splitDocumentsIntoColumns(renderingStyleDocumentation);

  return (
    <>
      <Link to="#" style={linkStyle} onClick={handleShow}>
        Conditions of Use
      </Link>

      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Conditions of Use</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className="placeholder-glow" aria-hidden="true">
              {/* Skeleton for paragraphs */}
              {Array.from({ length: 4 }, (_, index) => (
                <div key={`skeleton-paragraph-${index}`} className="mb-3">
                  <span className="placeholder col-12 mb-1"></span>
                  <span className="placeholder col-11 mb-1"></span>
                  <span className="placeholder col-10 mb-1"></span>
                  <span className="placeholder col-9"></span>
                </div>
              ))}
              {/* Skeleton for document links section (only show when loading documentation for non-vendor users) */}
              {isLoadingDocumentation && (
                <>
                  <hr className="my-3" />
                  <div className="placeholder-glow mb-2">
                    <span className="placeholder col-4"></span>
                  </div>
                  <hr className="my-3" />
                  <div className="row">
                    <div className="col-lg-6">
                      {Array.from({ length: 3 }, (_, index) => (
                        <div key={`skeleton-doc-left-${index}`} className="mb-2">
                          <span className="placeholder col-8"></span>
                        </div>
                      ))}
                    </div>
                    <div className="col-lg-6">
                      {Array.from({ length: 3 }, (_, index) => (
                        <div key={`skeleton-doc-right-${index}`} className="mb-2">
                          <span className="placeholder col-8"></span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {error && (
            <div className="alert alert-danger">
              Failed to load conditions of use data. Please try again.
            </div>
          )}
          
          {!loading && !error && (
            <>
              <div dangerouslySetInnerHTML={{ __html: termsContent }} />
              
              {isVendorUser && termsContent && (
                <></>
              )}
              {mtobDocuments.length > 0 && (
                <>
                  <hr />
                  <h5>Global Master Terms of Business &amp; Product Schedules</h5>
                  <hr />
                  <div className="row">
                    <div className="col-lg-6">
                      {mtobLeftColumn.map((document_: any) => {
                        const document__ = buildMtobDocument(document_, mtobFallbackRenderStyleId);
                        return (
                        <div key={document_.docId ?? document_.propertyName} className="ao_condOfUseDoc">
                          <Link
                            to={getDocumentHref(document__)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => {
                              handleDocumentLinkClick(event, document__);
                              if (!isPdfExtension(resolveDocumentExtension(document__))) sendDocumentData(document__);
                            }}
                          >
                            <img
                               src={document_.icon ? `/assets/images/fileicons/${document_.icon}` : '/assets/images/fileicons/105.svg'}
                              alt="Terms"
                              width="16"
                            />
                            {document_.fileName ?? document_.propertyVal ?? 'Document'}
                          </Link>
                        </div>
                        );
                      })}
                    </div>
                    <div className="col-lg-6">
                      {mtobRightColumn.map((document_: any) => {
                        const document__ = buildMtobDocument(document_, mtobFallbackRenderStyleId);
                        return (
                        <div key={document_.docId ?? document_.propertyName} className="ao_condOfUseDoc">
                          <Link
                            to={getDocumentHref(document__)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => {
                              handleDocumentLinkClick(event, document__);
                              if (!isPdfExtension(resolveDocumentExtension(document__))) sendDocumentData(document__);
                            }}
                          >
                            <img
                              src={document_.icon ? `/assets/images/fileicons/${document_.icon}` : '/assets/images/fileicons/105.svg'}
                              alt="Terms"
                              width="16"
                            />
                            {document_.fileName ?? document_.propertyVal ?? 'Document'}
                          </Link>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {!isVendorUser && renderingStyleDocumentation.length > 0 && (
                <>
                  <hr />
                  <h5>Standard Terms of Business</h5>
                  <hr />
                  <div className="row">
                    <div className="col-lg-6">
                      {leftColumn.map((document_) => (
                        <div key={document_.docID ?? `${document_.fileName}-${document_.fileTypeID}`} className="ao_condOfUseDoc">
                            <Link
                          to={getDocumentHref(document_)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => {
                            handleDocumentLinkClick(event, document_);
                            if (!isPdfExtension(resolveDocumentExtension(document_))) sendDocumentData(document_);
                          }}
                        >
                            <img
                              src={document_.icon ? `/assets/images/fileicons/${document_.icon}` : '/assets/images/fileicons/105.svg'}
                              alt="Terms"
                              width="16"
                            />
                            {document_.fileName ?? 'Document'}
                          </Link>
                        </div>
                      ))}
                    </div>
                    <div className="col-lg-6">
                      {rightColumn.map((document_) => (
                        <div key={document_.docID ?? `${document_.fileName}-${document_.fileTypeID}`} className="ao_condOfUseDoc">
                        <Link
                          to={getDocumentHref(document_)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => {
                            handleDocumentLinkClick(event, document_);
                            if (!isPdfExtension(resolveDocumentExtension(document_))) sendDocumentData(document_);
                          }}
                        >
                            <img
                              src={document_.icon ? `/assets/images/fileicons/${document_.icon}` : '/assets/images/fileicons/105.svg'}
                              alt="Terms"
                              width="16"
                            />
                            {document_.fileName ?? 'Document'}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-primary"
            id="LogOutContinue"
            onClick={handleClose}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ConditionOfUseModal;
